import { Injectable } from '@angular/core';
import { countMetric, flushMetrics, isMetricsEnabled, recordValue } from 'src/app/common/metrics/metrics';
import { AppReadyEvent } from '../../models/app-ready-event';
import { SyncProgressEvent } from '../../models/sync-progress-event';
import { DatabaseService } from '../database.service';
import { EventService } from '../event.service';

/**
 * Wertet den Sync aus und gibt am Ende einen Messbericht aus.
 *
 * Das gesamte Sync-Wissen steckt hier; der Metrics-Kern bleibt domänenfrei und damit für
 * andere Vorgänge nutzbar. Die Auswertung stützt sich ausschließlich auf bereits
 * vorhandene Ereignisse, es muss also kein Sync-Service angefasst werden.
 */
@Injectable({ providedIn: 'root' })
export class SyncMetricsService {
  private started = false;
  private reported = false;
  private lastProgressByService = new Map<string, number>();

  constructor(
    private readonly eventService: EventService<SyncProgressEvent | AppReadyEvent>,
    private readonly databaseService: DatabaseService
  ) {}

  /**
   * Beginnt die Auswertung. Mehrfache Aufrufe sind wirkungslos.
   * Kehrt sofort zurück und darf nie erwartet werden, damit der App-Start nicht verzögert wird.
   *
   * Bei abgeschalteter Messung wird gar nicht erst abonniert. Die einzelnen Aufzeichnungs-
   * funktionen prüfen das zwar ebenfalls, aber so bleibt im Normalbetrieb kein Listener und
   * keine Buchführung je Fortschrittsereignis übrig. Ein späterer Aufruf greift weiterhin,
   * falls die Messung zur Laufzeit eingeschaltet wurde.
   */
  public start(): void {
    if (this.started || !isMetricsEnabled()) {
      return;
    }
    this.started = true;

    this.eventService.events.subscribe(event => {
      if (event instanceof SyncProgressEvent) {
        this.onSyncProgress(event);
        return;
      }
      if (event instanceof AppReadyEvent) {
        void this.onAppReady();
      }
    });
  }

  private onSyncProgress(event: SyncProgressEvent): void {
    const previousProgress = this.lastProgressByService.get(event.title);

    if (previousProgress === undefined) {
      recordValue(`sync/${event.title}/announcedTotal`, event.total);
    } else {
      countMetric(`sync/${event.title}/pages`);
      countMetric(`sync/${event.title}/records`, event.progress - previousProgress);
    }

    this.lastProgressByService.set(event.title, event.progress);
  }

  private async onAppReady(): Promise<void> {
    if (this.reported || !isMetricsEnabled()) {
      return;
    }
    this.reported = true;

    // performance.now() zählt ab Seitenstart, der Wert hier ist also die Startdauer.
    recordValue('startup/appReadyMs', performance.now());

    await this.collectStorageInformation();
    flushMetrics('app-ready');
  }

  /**
   * Zählt die lokalen Datenbanken aus. Bewusst erst nach `AppReadyEvent`: Der Aufruf setzt
   * pro Datenbank zwei Zählabfragen ab und würde während des Syncs das Ergebnis verfälschen.
   */
  private async collectStorageInformation(): Promise<void> {
    try {
      const storageInformation = await this.databaseService.getStorageInformation();
      for (const storage of storageInformation ?? []) {
        recordValue(`db/${storage.name}/rows`, storage.dbCount);
        if (storage.indexCount != null) {
          recordValue(`db/${storage.name}/indexRows`, storage.indexCount);
        }
        if (storage.documentCount != null) {
          recordValue(`db/${storage.name}/serverDocuments`, storage.documentCount);
        }
      }
    } catch (error) {
      window.logger.error('SYNCMETRICSSERVICE COLLECTSTORAGEINFORMATION', error);
    }
  }
}
